import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Bot, CheckCircle, XCircle, Edit, Send, 
  User, Package, Clock, Loader2, MessageSquare, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AIDrafts() {
  const [user, setUser] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editedResponse, setEditedResponse] = useState('');
  const [isActioning, setIsActioning] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    if (userData?.role === 'seller') {
      fetchDrafts();
    }
  }, []);

  const fetchDrafts = async () => {
    try {
      const response = await axios.get(`${API}/ai-drafts?status=pending`);
      setDrafts(response.data);
    } catch (error) {
      toast.error('Failed to fetch AI drafts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (draftId, action, editedMsg = null) => {
    setIsActioning(true);
    try {
      const payload = { action };
      if (editedMsg) payload.edited_response = editedMsg;
      
      await axios.patch(`${API}/ai-drafts/${draftId}`, payload);
      
      if (action === 'approve') {
        toast.success('Response approved and sent to buyer!');
      } else if (action === 'edit') {
        toast.success('Response edited and sent to buyer!');
      } else if (action === 'reject') {
        toast.info('Draft rejected');
      }
      
      setDrafts(drafts.filter(d => d.id !== draftId));
      setShowEditDialog(false);
      setSelectedDraft(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process draft');
    } finally {
      setIsActioning(false);
    }
  };

  const openEditDialog = (draft) => {
    setSelectedDraft(draft);
    setEditedResponse(draft.ai_response);
    setShowEditDialog(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (user?.role !== 'seller') {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold text-primary mb-4">AI Response Drafts</h1>
          <p className="text-muted-foreground">This feature is available for sellers only.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="ai-drafts-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2 flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-indigo-500" />
            AI Response Drafts
          </h1>
          <p className="text-muted-foreground">
            Review and approve AI-generated responses before they're sent to buyers
          </p>
        </div>

        {/* Info Card */}
        <Card className="mb-6 bg-indigo-50 border-indigo-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Bot className="h-5 w-5 text-indigo-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-indigo-900">How It Works</p>
                <p className="text-sm text-indigo-700 mt-1">
                  When buyers send inquiries, AI generates a draft response based on your product details 
                  and knowledge base. Review each draft, then approve as-is, edit before sending, or reject.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : drafts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-medium mb-2">All Caught Up!</h3>
              <p className="text-muted-foreground">
                No pending AI drafts. New drafts will appear here when buyers send inquiries.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {drafts.map((draft) => (
              <Card key={draft.id} data-testid={`ai-draft-${draft.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        {draft.product_title}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-4 mt-1">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {draft.buyer_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(draft.created_at)}
                        </span>
                      </CardDescription>
                    </div>
                    <Badge className="bg-amber-500">Pending Review</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Buyer's Message */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="h-4 w-4 text-blue-600" />
                        <p className="text-xs font-medium text-blue-800">Buyer's Inquiry</p>
                      </div>
                      <p className="text-sm text-blue-900">{draft.buyer_message}</p>
                    </div>
                    
                    {/* AI Draft Response */}
                    <div className="p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-2 mb-2">
                        <Bot className="h-4 w-4 text-indigo-600" />
                        <p className="text-xs font-medium text-slate-700">AI Generated Response</p>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{draft.ai_response}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2">
                      <Button 
                        onClick={() => handleAction(draft.id, 'approve')}
                        disabled={isActioning}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        data-testid={`approve-btn-${draft.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve & Send
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => openEditDialog(draft)}
                        disabled={isActioning}
                        data-testid={`edit-btn-${draft.id}`}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button 
                        variant="ghost"
                        onClick={() => handleAction(draft.id, 'reject')}
                        disabled={isActioning}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`reject-btn-${draft.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Response</DialogTitle>
              <DialogDescription>
                Customize the response before sending to {selectedDraft?.buyer_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Show buyer's message for context */}
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p className="font-medium text-blue-800 mb-1">Buyer asked:</p>
                <p className="text-blue-900">{selectedDraft?.buyer_message}</p>
              </div>
              
              <Textarea
                value={editedResponse}
                onChange={(e) => setEditedResponse(e.target.value)}
                rows={10}
                className="font-mono text-sm"
                data-testid="edit-response-textarea"
              />
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleAction(selectedDraft?.id, 'edit', editedResponse)}
                  disabled={isActioning}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="save-edit-btn"
                >
                  {isActioning ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" /> Save & Send</>
                  )}
                </Button>
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
